import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  deleteAccount: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log("[AuthContext] Auth state changed:", event, session?.user?.id);
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Handle logout event specifically
        if (event === 'SIGNED_OUT') {
          console.log("[AuthContext] User signed out, clearing state");
          setSession(null);
          setUser(null);
          // Clear local storage
          localStorage.clear();
          sessionStorage.clear();
          // Force redirect to home page
          window.location.href = '/';
        }
      }
    );

    const getInitialSession = async () => {
      try {
        console.log("[AuthContext] Getting initial session");
        const { data } = await supabase.auth.getSession();
        console.log("[AuthContext] Initial session:", data?.session?.user?.id || "No session");
        setSession(data.session);
        setUser(data.session?.user ?? null);
      } catch (error) {
        console.error("[AuthContext] Error getting initial session:", error);
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    console.log("[AuthContext] Starting sign out process");
    try {
      setLoading(true);
      
      // Clear local state immediately
      setSession(null);
      setUser(null);
      
      // Clear all storage
      localStorage.clear();
      sessionStorage.clear();
      
      // Call Supabase signOut
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error("[AuthContext] Supabase signOut error:", error);
        // Even if there's an error, we still want to clear everything locally
      }
      
      console.log("[AuthContext] Sign out completed, redirecting");
      
      // Force navigation to home page
      window.location.href = '/';
      
    } catch (error) {
      console.error("[AuthContext] Error signing out:", error);
      
      // Still force logout locally and redirect
      setSession(null);
      setUser(null);
      localStorage.clear();
      sessionStorage.clear();
      
      toast.error("There was an issue signing out, but you've been logged out locally");
      
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    }
  };

  const deleteAccount = async (): Promise<boolean> => {
    console.log("[AuthContext] Deleting account");
    try {
      const { data, error } = await supabase.functions.invoke('delete-account');
      
      if (error) {
        console.error("[AuthContext] Error deleting account:", error);
        toast.error("Failed to delete account");
        return false;
      }

      if (data?.success) {
        console.log("[AuthContext] Account deletion successful");
        toast.success("Account deleted successfully");
        return true;
      } else {
        console.error("[AuthContext] Account deletion failed:", data?.error);
        toast.error(data?.error || "Failed to delete account");
        return false;
      }
    } catch (error) {
      console.error("[AuthContext] Error deleting account:", error);
      toast.error("Failed to delete account");
      return false;
    }
  };

  const value = {
    session,
    user,
    loading,
    signOut,
    deleteAccount
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
